import React, { useEffect, useState, useRef, useMemo } from 'react';
import { CircularProgress, Menu, MenuItem } from '@mui/material';
import { Maximize2, Minimize2, EllipsisVertical, Download, TableProperties, ChartLine, ImageDown } from 'lucide-react';
import ReactECharts from 'echarts-for-react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useSelector } from 'react-redux';
import { selectedDeviceSelector } from '../../store/api/devices/selectors';
import { IFullDeviceDetails } from '../../store/api/deviceConfig/types';
import { usePerformanceFilters } from '../../pages/DeviceDetail/Performance/PerformanceContext';
import { ThemeColorName, getThemeAwareChartColors } from '../../theme/chartColors';
import { useIsDarkMode } from '../../theme/hooks';
import { LOCAL_STORAGE } from '../../globals/globals';

dayjs.extend(utc);

export interface ChartSeriesConfig {
  name: string;
  type: 'bar' | 'line';
  yAxisIndex: 0 | 1;
  color: ThemeColorName | string;  // Support both semantic names and hex codes
  dataKey: string;
  isPercentage?: boolean;
}

export interface ChartAxisConfig {
  name: string;
  position: 'left' | 'right';
  min?: number;
  max?: number;
  formatter?: string;
  nameGap?: number;
}

export interface SiteChartsConfig {
  title: string;
  apiEndpoint: string;
  csvFilename: string;
  chartImageFilename: string;
  series: ChartSeriesConfig[];
  yAxes: [ChartAxisConfig, ChartAxisConfig];
  tableColumns: {
    header: string;
    dataKey: string;
    isPercentage?: boolean;
  }[];
  divisor?: number;  // Optional: divisor to scale raw values (e.g., 1000 to convert to dBm)
}

export interface ChartGridSize {
  xs?: number;
  sm?: number;
  md?: number;
  lg?: number;
  xl?: number;
}

interface SiteChartsProps {
  config: SiteChartsConfig;
  onFullscreenToggle: () => void;
  isFullscreen: boolean;
  gridSize?: ChartGridSize;  // Optional: custom grid sizing for flexible layouts
  additionalParams?: Record<string, string>;  // Optional: additional query parameters (e.g., cell_name)
}

const SiteCharts: React.FC<SiteChartsProps> = ({ 
  config,
  onFullscreenToggle, 
  isFullscreen,
  gridSize,  // Not used internally, but exposed for documentation
  additionalParams  // Additional query parameters (e.g., cell_name for cell charts)
}) => {
  const selectedDevice: IFullDeviceDetails = useSelector(selectedDeviceSelector);
  const { timeRange, truncate, startDate, endDate, refreshTrigger } = usePerformanceFilters();
  
  const isDarkMode = useIsDarkMode();
  const themeColors = useMemo(() => getThemeAwareChartColors(), [isDarkMode]);
  
  const [chartData, setChartData] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  
  // Use sessionStorage to persist table view preference across component instances
  const storageKey = `chart-view-${config.title}`;
  const [showTable, setShowTable] = useState<boolean>(() => {
    const stored = sessionStorage.getItem(storageKey);
    return stored === 'true';
  });
  
  const chartRef = useRef<ReactECharts>(null);
  const menuOpen = Boolean(anchorEl);
  const prevRefreshTriggerRef = useRef<number>(refreshTrigger);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleToggleView = () => {
    const newValue = !showTable;
    setShowTable(newValue);
    sessionStorage.setItem(storageKey, String(newValue));
    handleMenuClose();
  };

  const handleExportCSV = () => {
    if (chartData.length === 0) {
      alert('No data available to export');
      return;
    }

    // Create CSV headers
    const headers = ['Timestamp', ...config.tableColumns.map(col => col.header)];
    
    // Create CSV rows
    const rows = chartData.map(d => {
      const row = [dayjs.utc(d.timestamp).format('YYYY-MM-DD HH:mm:ss')];
      config.tableColumns.forEach(col => {
        const value = d[col.dataKey];
        row.push(col.isPercentage ? value.toFixed(2) : value);
      });
      return row;
    });

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `${config.csvFilename}_${dayjs().format('YYYY-MM-DD_HH-mm-ss')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    handleMenuClose();
  };

  const handleSaveAsImage = () => {
    const chartInstance = chartRef.current?.getEchartsInstance();
    if (chartInstance) {
      const url = chartInstance.getDataURL({
        type: 'png',
        pixelRatio: 2,
        backgroundColor: themeColors.background
      });
      const link = document.createElement('a');
      link.href = url;
      link.download = `${config.chartImageFilename}_${dayjs().format('YYYY-MM-DD_HH-mm-ss')}.png`;
      link.click();
    }
    handleMenuClose();
  };

  // Debug: Track dependency changes
  const prevDepsRef = useRef({
    serialNumber: selectedDevice?.serialNumber,
    timeRange,
    truncate,
    startDate,
    endDate,
    apiEndpoint: config.apiEndpoint,
    title: config.title,
    additionalParams,
    refreshTrigger
  });

  useEffect(() => {
    const prev = prevDepsRef.current;
    const changes: string[] = [];
    
    if (prev.serialNumber !== selectedDevice?.serialNumber) {
      changes.push(`serialNumber: ${prev.serialNumber} → ${selectedDevice?.serialNumber}`);
    }
    if (prev.timeRange !== timeRange) {
      changes.push(`timeRange: ${prev.timeRange} → ${timeRange}`);
    }
    if (prev.truncate !== truncate) {
      changes.push(`truncate: ${prev.truncate} → ${truncate}`);
    }
    if (prev.startDate !== startDate) {
      changes.push(`startDate: ${prev.startDate?.format('YYYY-MM-DD')} → ${startDate?.format('YYYY-MM-DD')}`);
    }
    if (prev.endDate !== endDate) {
      changes.push(`endDate: ${prev.endDate?.format('YYYY-MM-DD')} → ${endDate?.format('YYYY-MM-DD')}`);
    }
    if (prev.apiEndpoint !== config.apiEndpoint) {
      changes.push(`apiEndpoint: ${prev.apiEndpoint} → ${config.apiEndpoint}`);
    }
    if (prev.title !== config.title) {
      changes.push(`title: ${prev.title} → ${config.title}`);
    }
    if (prev.additionalParams !== additionalParams) {
      changes.push(`additionalParams: ${JSON.stringify(prev.additionalParams)} → ${JSON.stringify(additionalParams)}`);
    }
    if (prev.refreshTrigger !== refreshTrigger) {
      changes.push(`refreshTrigger: ${prev.refreshTrigger} → ${refreshTrigger} ⏰ (auto-refresh)`);
    }

    if (changes.length > 0) {
      console.log(`🔄 [${config.title}] useEffect triggered. Changes:`, changes);
    }

    // Update ref for next comparison
    prevDepsRef.current = {
      serialNumber: selectedDevice?.serialNumber,
      timeRange,
      truncate,
      startDate,
      endDate,
      apiEndpoint: config.apiEndpoint,
      title: config.title,
      additionalParams,
      refreshTrigger
    };
  }, [selectedDevice?.serialNumber, timeRange, truncate, startDate, endDate, config.apiEndpoint, config.title, additionalParams, refreshTrigger]);

  useEffect(() => {
    const fetchChartData = async () => {
      if (!selectedDevice?.serialNumber) {
        setError('No device selected');
        setLoading(false);
        return;
      }

      // If custom range is selected but dates are not set, keep showing current data
      if (timeRange === 'custom' && (!startDate || !endDate)) {
        return; // Don't fetch, just keep existing data
      }

      const isBackgroundRefresh = prevRefreshTriggerRef.current !== refreshTrigger;
      prevRefreshTriggerRef.current = refreshTrigger;
      if (!isBackgroundRefresh) setLoading(true);
      setError(null);

      try {
        const params: Record<string, string> = {
          site_name: selectedDevice.serialNumber,
          truncate: truncate,
        };

        // Add additional parameters (e.g., cell_name for cell charts)
        if (additionalParams) {
          Object.assign(params, additionalParams);
        }

        // Add date range parameters based on selection
        if (timeRange === 'custom' && startDate && endDate) {
          params.start_date = startDate.startOf('day').format('YYYY-MM-DD');
          params.end_date = endDate.startOf('day').format('YYYY-MM-DD');
        } else {
          params.time_range = timeRange;
        }

        // Use the gateway proxy path to route through devices-api to python-chart-api
        // This ensures proper authentication and routing through the gateway
        const apiBaseUrl = '/v1/charts';
        
        // Get JWT token from localStorage for authentication
        const token = localStorage.getItem(LOCAL_STORAGE.TOKEN);
        
        const response = await fetch(`${apiBaseUrl}${config.apiEndpoint}?${new URLSearchParams(params)}`, {
          credentials: 'include',
          headers: {
            'Accept': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` }),
          },
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        
        setChartData(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch data');
      } finally {
        setLoading(false);
      }
    };

    fetchChartData();
  }, [selectedDevice?.serialNumber, timeRange, truncate, startDate, endDate, config.apiEndpoint, config.title, additionalParams, refreshTrigger]);

  // Calculate chart height with appropriate constraints
  // For fullscreen: Use available viewport height minus header/controls (capped for drawer contexts)
  // For normal view: Fixed 320px height
  const chartHeight = isFullscreen 
    ? Math.min(window.innerHeight - 350, 475)  // Cap at 450px to fit in drawer
    : 320;

  // Prepare ECharts option
  const getOption = () => {
    if (chartData.length === 0) return {};

    const tooltipTsFormat = truncate === 'minute' ? 'MMM DD hh:mm A' : truncate === 'hour' ? 'MMM DD hh:00 A' : 'MMM DD';
    const timestamps = chartData.map(d => dayjs.utc(d.timestamp).format('MMM DD'));
    const tooltipTimestamps = chartData.map(d => dayjs.utc(d.timestamp).format(tooltipTsFormat));
    
    // Helper function to resolve color (semantic name or hex code)
    const resolveColor = (color: ThemeColorName | string): string => {
      // Check if it's a semantic color name — use theme-aware CSS variable value
      if (color in themeColors) {
        return (themeColors as Record<string, string>)[color];
      }
      // Otherwise return as-is (hex code)
      return color;
    };
    
    // Prepare series data
    const seriesData = config.series.map(seriesConfig => {
      const resolvedColor = resolveColor(seriesConfig.color);
      
      return {
        name: seriesConfig.name,
        type: seriesConfig.type,
        yAxisIndex: seriesConfig.yAxisIndex,
        data: chartData.map(d => d[seriesConfig.dataKey]),
        itemStyle: {
          color: resolvedColor
        },
        ...(seriesConfig.type === 'line' ? {
          smooth: false,
          lineStyle: {
            color: resolvedColor,
            width: 2
          },
          symbol: 'circle',
          symbolSize: 6
        } : {}),
        emphasis: {
          focus: 'series'
        }
      };
    });

    return {
      tooltip: {
        trigger: 'axis',
        backgroundColor: themeColors.tooltipBg,
        borderColor: themeColors.cardBorder,
        textStyle: { color: themeColors.foreground },
        axisPointer: {
          type: 'cross',
          crossStyle: {
            color: themeColors.axisLabel
          }
        },
        formatter: (params: any) => {
          const label = tooltipTimestamps[params[0].dataIndex] ?? params[0].axisValue;
          let result = `<strong>${label}</strong><br/>`;
          params.forEach((param: any) => {
            // Skip undefined or null values
            if (param.value === undefined || param.value === null) {
              return;
            }
            const seriesConfig = config.series.find(s => s.name === param.seriesName);
            const value = seriesConfig?.isPercentage
              ? `${param.value.toFixed(2)}%` 
              : param.value.toLocaleString();
            result += `${param.marker} ${param.seriesName}: ${value}<br/>`;
          });
          return result;
        }
      },
      toolbox: {
        feature: {
          dataZoom: {
            yAxisIndex: 'none'
          },
          restore: {}
        },
        iconStyle: {
          color: 'none',
          borderColor: themeColors.foreground,
          borderWidth: 1
        },
        emphasis: {
          iconStyle: {
            color: 'none',
            borderColor: themeColors.foreground,
            borderWidth: 1
          }
        },
        right: 20,
        top: 5
      },
      legend: {
        data: config.series.map(s => s.name),
        top: 35,
        itemGap: 20,
        textStyle: {
          fontSize: 12,
          color: themeColors.foreground
        }
      },
      grid: {
        left: 80,
        right: 80,
        bottom: 50,
        top: 70,
        containLabel: false
      },
      xAxis: {
        type: 'category',
        data: timestamps,
        axisPointer: {
          type: 'shadow'
        },
        axisLabel: {
          fontSize: 11,
          color: themeColors.axisLabel
        },
        axisLine: {
          lineStyle: { color: themeColors.gridLine }
        }
      },
      yAxis: config.yAxes.map(axis => ({
        type: 'value',
        name: axis.name,
        position: axis.position,
        nameLocation: 'middle',
        nameGap: axis.nameGap || 50,
        min: axis.min,
        max: axis.max,
        nameTextStyle: {
          fontSize: 12,
          color: themeColors.axisLabel,
          align: 'center'
        },
        nameRotate: 90,
        axisLabel: {
          formatter: axis.formatter || '{value}',
          fontSize: 11,
          color: themeColors.axisLabel
        },
        axisLine: {
          lineStyle: { color: themeColors.gridLine }
        },
        splitLine: {
          show: axis.position === 'left',
          lineStyle: {
            type: 'solid',
            color: themeColors.gridLine,
            opacity: 0.5
          }
        }
      })),
      series: seriesData,
      dataZoom: [
        {
          type: 'inside'
        },
        {
          type: 'slider',
          height: 20,
          bottom: 10
        }
      ]
    };
  };

  return (
    <div
      className="rounded-xl border border-card-border bg-card text-card-foreground shadow-sm px-4 pb-4 pt-2"
      style={{
        height: isFullscreen ? Math.min(window.innerHeight - 250, 600) : 'auto',
      }}
    >
      <div className="flex justify-between items-center mb-1">
        <h2 className="text-base font-semibold text-foreground">
          {config.title}
        </h2>
        <div className="flex gap-1">
          <button
            onClick={onFullscreenToggle}
            className="h-8 w-8 flex items-center justify-center rounded-md bg-background text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
          >
            {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
          </button>
          <button
            onClick={handleMenuOpen}
            className="h-8 w-8 flex items-center justify-center rounded-md bg-background text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
            aria-label="chart options"
            aria-controls={menuOpen ? 'chart-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={menuOpen ? 'true' : undefined}
          >
            <EllipsisVertical className="h-4 w-4" />
          </button>
        </div>
      </div>

      <Menu
        id="chart-menu"
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        MenuListProps={{
          'aria-labelledby': 'chart-options-button',
        }}
        slotProps={{
          paper: {
            sx: {
              backgroundColor: 'var(--color-popover, var(--popover))',
              color: 'var(--color-foreground, var(--foreground))',
              borderColor: 'var(--color-border, var(--border))',
              border: '1px solid',
            }
          }
        }}
      >
        <MenuItem onClick={handleToggleView} sx={{ color: 'inherit' }}>
          {showTable ? <ChartLine className="h-4 w-4 mr-2" /> : <TableProperties className="h-4 w-4 mr-2" />}
          {showTable ? 'Show as Chart' : 'Show as Table'}
        </MenuItem>
        <MenuItem onClick={handleExportCSV} disabled={chartData.length === 0} sx={{ color: 'inherit' }}>
          <Download className="h-4 w-4 mr-2" />
          Export as CSV
        </MenuItem>
        <MenuItem onClick={handleSaveAsImage} disabled={chartData.length === 0 || showTable} sx={{ color: 'inherit' }}>
          <ImageDown className="h-4 w-4 mr-2" />
          Save as Image
        </MenuItem>
      </Menu>

      {loading && (
        <div className="flex justify-center items-center" style={{ height: chartHeight }}>
          <CircularProgress sx={{ color: 'var(--color-primary, var(--primary))' }} />
        </div>
      )}

      {error && !loading && (
        <div className="rounded-md border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive mb-2">
          {error}
        </div>
      )}

      {!loading && !error && chartData.length === 0 && (
        <div className="rounded-md border border-border bg-muted px-4 py-3 text-sm text-muted-foreground">
          No data available for the selected time range
        </div>
      )}

      {!loading && !error && chartData.length > 0 && !showTable && (
        <ReactECharts
          ref={chartRef}
          option={getOption()}
          style={{ height: `${chartHeight}px`, width: '100%' }}
          notMerge={false}
          lazyUpdate={true}
          theme="default"
        />
      )}

      {!loading && !error && chartData.length > 0 && showTable && (
        <div className="overflow-auto rounded-md border border-border" style={{ maxHeight: chartHeight }}>
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-muted">
              <tr>
                <th className="px-3 py-2 text-left font-semibold text-foreground border-b border-border">Timestamp</th>
                {config.tableColumns.map((col, idx) => (
                  <th key={idx} className="px-3 py-2 text-left font-semibold text-foreground border-b border-border">{col.header}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {chartData.map((row, index) => (
                <tr key={index} className="hover:bg-accent/50 transition-colors">
                  <td className="px-3 py-2 text-foreground border-b border-border">{dayjs.utc(row.timestamp).format('MMM DD, YYYY HH:mm')}</td>
                  {config.tableColumns.map((col, idx) => (
                    <td key={idx} className="px-3 py-2 text-foreground border-b border-border">
                      {col.isPercentage 
                        ? `${row[col.dataKey].toFixed(2)}%` 
                        : row[col.dataKey].toLocaleString()}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default SiteCharts;

